/**
 * API-related constants for the Saidline application
 * Centralized configuration for HTTP requests, timeouts, and retry logic
 */

export const API_CONSTANTS = {
    /**
     * HTTP request timeouts in milliseconds
     */
    TIMEOUTS: {
        DEFAULT: 30000, // 30 seconds for regular requests
        TRAINING_SEARCH: 90000, // 90 seconds for AI training generation
        DATA_SYNC: 300000, // 5 minutes for the full production-to-testing DB sync
    },

    /**
     * Retry configuration for failed requests
     */
    RETRY: {
        DEFAULT_COUNT: 3,
        MAX_COUNT: 5,
        DELAY_MS: 1000,
        BACKOFF_MULTIPLIER: 2,
        MAX_DELAY_MS: 10000,
    },

    /**
     * List of HTTP status codes considered retryable.
     * These errors typically indicate temporary issues (e.g. network instability, server overload)
     * and are safe to retry after a short delay.
     *
     * Codes:
     *  - 408 Request Timeout: The server did not receive a complete request in time.
     *  - 429 Too Many Requests: The client has sent too many requests in a given timeframe (rate limiting).
     *  - 500 Internal Server Error: A generic error occurred on the server side.
     *  - 502 Bad Gateway: The server received an invalid response from an upstream service.
     *  - 503 Service Unavailable: The server is temporarily unable to handle the request (e.g. maintenance or overload).
     *  - 504 Gateway Timeout: The server did not receive a timely response from an upstream server.
     */
    RETRYABLE_STATUS_CODES: [408, 429, 500, 502, 503, 504],

    /**
     * Status codes that should not trigger user notifications
     *
     * Codes:
     * - 401 Unauthorized: job for Auth.interceptor
     */
    SILENT_STATUS_CODES: [401],

    /**
     * Requests that should not trigger user notifications or error handling
     */
    SILENT_URLS: ['/api/v1/health', '/api/v1/auth/refresh'],

    /**
     * Headers that should not be logged (redacted for privacy/security)
     */
    SENSITIVE_HEADERS: ['authorization', 'x-api-key', 'cookie', 'set-cookie'],

    /**
     * Safe HTTP methods that can be retried
     */
    SAFE_HTTP_METHODS: ['GET', 'HEAD', 'OPTIONS'],

    /**
     * API endpoints configuration
     */
    ENDPOINTS: {
        AUTH: '/auth',
        LEGAL: '/legal',
        BLOG: '/blog',
        USER: '/user',
        TRAINING: '/training',
        CALENDAR: '/calendar',
        SQUAD: '/squad',
        BILLING: '/billing',
        SUPPORT: '/support',
        ADMIN: {
            DATASET: '/admin/dataset',
            VECTOR_TEST: '/admin/vector-test',
            TEAM: '/admin/team',
            DATA_SYNC: '/admin/data-sync',
        },
    },

    /** How many past sessions the history page shows. */
    HISTORY: {
        RECENT_LIMIT: 5,
    },
};
